import { QueueingTypes } from '../AppConstants'
import type { ScheduleOption } from '../types'
import { ApprovalMode } from '~publish/components/Composer/save/types'
import type { ComposerQueueingType } from '../stores/types'

type GetComposerConfirmationMessageArgs = {
  entityName: 'post' | 'thread'
  draftMode: boolean
  queueingType: NonNullable<ComposerQueueingType>
  savedPostStatus?: string
  isPlural?: boolean
  approvalMode?: ApprovalMode
  statusChange?: 'post' | 'draft'
  scheduleSelectorChange?: ScheduleOption
}

export const getComposerConfirmationMessage = (
  args: GetComposerConfirmationMessageArgs,
): string => {
  const {
    entityName,
    draftMode,
    queueingType,
    savedPostStatus,
    isPlural = false,
    approvalMode,
    statusChange,
    scheduleSelectorChange,
  } = args

  const isDraft =
    (statusChange !== 'post' && draftMode) || statusChange === 'draft'

  // Only for the queueing type SAVE
  const savePostSingularCopy = `Your ${entityName} has been saved.`
  const savePostPluralCopy = 'All your posts have been saved.'
  const saveDraftSingularCopy = 'Your draft is saved.'
  const saveDraftPluralCopy = 'Your drafts are saved.'

  // Only for the queueing type NOW
  const shareNowSingularCopy =
    savedPostStatus === 'sent'
      ? `Your ${entityName} is live. 🎉`
      : `Preparing your ${entityName}. It will go live shortly.`
  const shareNowPluralCopy =
    savedPostStatus === 'sent'
      ? 'All your posts are live. 🎉'
      : 'All posts have been prepared for publishing. They will go live shortly.'

  // The queueing types QUEUE, NEXT, NOW, and CUSTOM all happen in the create a new Post flow
  // The queueing types ADD_DRAFT, NEXT_DRAFT, and CUSTOM_DRAFT all happen in create Draft flow
  // The queueing types SAVE all happen in edit both Post and Draft flow
  const successfulResponseMessageMap = new Map([
    [
      QueueingTypes.QUEUE,
      {
        singular: `Your ${entityName} has been added to your queue.`,
        plural: 'All your posts have been added to your queue.',
      },
    ],
    [
      QueueingTypes.NEXT,
      {
        singular: `Your ${entityName} has been added to your queue.`,
        plural: 'All your posts have been added to your queue.',
      },
    ],
    [
      QueueingTypes.NOW,
      {
        singular: shareNowSingularCopy,
        plural: shareNowPluralCopy,
      },
    ],
    [
      QueueingTypes.CUSTOM,
      {
        singular: `Your ${entityName} is scheduled.`,
        plural: 'All your posts are scheduled.',
      },
    ],
    [
      QueueingTypes.RECOMMENDED,
      {
        singular: `Your ${entityName} is scheduled.`,
        plural: 'All your posts are scheduled.',
      },
    ],
    [
      QueueingTypes.SAVE,
      {
        singular: isDraft ? saveDraftSingularCopy : savePostSingularCopy,
        plural: isDraft ? saveDraftPluralCopy : savePostPluralCopy,
      },
    ],
    [
      QueueingTypes.ADD_DRAFT,
      {
        singular: saveDraftSingularCopy,
        plural: saveDraftPluralCopy,
      },
    ],
    [
      QueueingTypes.NEXT_DRAFT,
      {
        singular: saveDraftSingularCopy,
        plural: saveDraftPluralCopy,
      },
    ],
    [
      QueueingTypes.CUSTOM_DRAFT,
      {
        singular: saveDraftSingularCopy,
        plural: saveDraftPluralCopy,
      },
    ],
  ])

  // Override the messages for approval mode
  if (approvalMode) {
    const singular =
      approvalMode === ApprovalMode.REQUEST_APPROVAL
        ? 'Your draft is saved, and the approval request is on its way.'
        : 'Your draft is saved, and the approval request was reverted.'
    const plural =
      approvalMode === ApprovalMode.REQUEST_APPROVAL
        ? 'Your drafts are saved, and the approval requests are on their way.'
        : 'Your drafts are saved, and the approval requests were reverted.'

    const overrideValues = [
      QueueingTypes.ADD_DRAFT,
      QueueingTypes.CUSTOM_DRAFT,
      QueueingTypes.NEXT_DRAFT,
      QueueingTypes.SAVE,
    ]
    overrideValues.forEach((overrideQueueingType) => {
      successfulResponseMessageMap.set(overrideQueueingType, {
        singular,
        plural,
      })
    })
  }

  let finalQueueingType = queueingType

  // Override the queueing type if the schedule selector is changed
  // to return the new messages from post actions
  if (scheduleSelectorChange) {
    if (scheduleSelectorChange === 'Now') {
      finalQueueingType = QueueingTypes.NOW
    } else if (scheduleSelectorChange === 'Queue') {
      finalQueueingType = QueueingTypes.QUEUE
    } else if (scheduleSelectorChange === 'Prioritize') {
      finalQueueingType = QueueingTypes.NEXT
    }
  }

  const messages = successfulResponseMessageMap.get(finalQueueingType)
  if (!messages) {
    throw new Error(`No message found for queueing type: ${finalQueueingType}`)
  }
  const message = isPlural ? messages.plural : messages.singular

  // Override the messages for the queueing type SAVE
  // when moving from a draft to a post and vice versa looking at the statusChange
  if (statusChange === 'post') {
    return message.replace('draft', 'post')
  } else if (statusChange === 'draft') {
    return message.replace('post', 'draft').replace('thread', 'draft')
  }
  return message
}
